import { QueryFunctionContext, useMutation, useQuery, UseQueryResult } from "@tanstack/react-query"

import { api } from "@/api"
import { Campaign } from "@/types/djangio"
import { ElectedOfficial } from "QuorumGrassroots/pages/campaign/elected-officials/ElectedOfficialsPage.types"
import { Candidate } from "QuorumGrassroots/pages/campaign/candidates/CandidatesPage.types"

export interface DeadlineInfo {
    date: string | null
    timestamp: string | null
    postmarked_or_received: string | null
}

export interface MailBallotDeadlines {
    postmarked_or_received: string | null
    received_no_later_than: DeadlineInfo | null
    return_in_person: DeadlineInfo | null
    return_by_mail: DeadlineInfo | null
    ballot_request: DeadlineInfo | null
}

export interface ByMailVotingInfo {
    explainer_url: string | null
    all_mail: unknown | null
    deadline: MailBallotDeadlines | null
}

export interface InPersonRegistrationInfo {
    at_polling_place_on_election_day: boolean | null
    deadline: DeadlineInfo | null
}

export interface OnlineRegistrationInfo {
    url: string | null
    deadline: DeadlineInfo | null
}

export interface ByMailRegistrationInfo {
    deadline: DeadlineInfo | null
}

export interface RegistrationInfo {
    explainer_url: string | null
    in_person: InPersonRegistrationInfo | null
    online: OnlineRegistrationInfo | null
    by_mail: ByMailRegistrationInfo | null
}

export interface EarlyVotingInfo {
    url: string | null
    start_date: string | null
    end_date: string | null
    varies: boolean | null
}

export interface VotingInfo {
    in_person_voting_available: boolean | null
    mail_ballots_sent_automatically: boolean | null
    early: EarlyVotingInfo | null
    by_mail: ByMailVotingInfo | null
    in_person: unknown | null
}

export interface Election {
    ocd_id: string
    created_at: string | null
    updated_at: string | null
    date: string
    election_type: string
    polling_location_url: string | null
    canonical_url: string | null
    website: string | null
    guidance_phase: string
    population: string | null
    voting: VotingInfo | null
    registration: RegistrationInfo | null
}

export interface ElectionCountdownResponse {
    days_to_election: number | null
    hours_to_election: number | null
    minutes_to_election: number | null
    seconds_to_election: number | null
    election_datetime: string | null
    election_day_start_in: string | null
    elections: Election[]
}

export interface FetchPlaceholdersFromMessageParams {
    content: string
    subject: string
    fixed_pre_content: string
    fixed_post_content: string
    person_id?: string
}

export const fetchPlaceholdersFromMessage = async (params: FetchPlaceholdersFromMessageParams) => {
    const response = await api.get({
        model: DjangIO.app.grassroots.campaign.models.GrassrootsCampaign,
        action: "replace_message_placeholder_with_legislator",
        params: params as unknown as Record<string, string>,
    })
    return response?.json?.()
}

export const fetchElectionCountdown = async (): Promise<ElectionCountdownResponse> => {
    const response = await api.get("/gotv/elections/")
    return response.json()
}

export const useFetchPlaceholdersFromMessage = (officialId?: string) => {
    const mutation = useMutation({
        mutationKey: ["fetchPlaceholdersFromMessage", officialId],
        mutationFn: (params: FetchPlaceholdersFromMessageParams) => fetchPlaceholdersFromMessage(params),
    })

    return mutation
}

const fetchCampaign = async ({ queryKey }: QueryFunctionContext<[string]>) => {
    const [campaignId] = queryKey
    return api
        .get({
            resourceUri: DjangIO.app.grassroots.campaign.models.GrassrootsCampaign.resourceUriFromId(campaignId),
            params: {
                decode_enums: "false",
                count: "false",
                dehydrate_extra: "",
            },
        })
        .then((resp) => resp.json()) as Promise<Campaign>
}

export const useCampaign = (campaignId: string) =>
    useQuery({
        queryKey: [campaignId],
        queryFn: fetchCampaign,
        enabled: Boolean(campaignId),
        staleTime: Infinity,
    })

const fetchCandidates = async (year: string) =>
    api.get(`/gotv/candidates/?year=${year}`).then((resp) => resp.json() ?? [])
export const useCandidates = (year: string): UseQueryResult<Candidate[], unknown> =>
    useQuery({
        queryKey: ["gotv/candidates", year],
        queryFn: () => fetchCandidates(year),
        enabled: Boolean(year),
        staleTime: Infinity,
    })

const fetchElectedOfficials = async () => api.get(`/gotv/elected-officials/`).then((resp) => resp.json() ?? [])
export const useElectedOfficials = (): UseQueryResult<ElectedOfficial[], unknown> =>
    useQuery({
        queryKey: ["gotv/elected-officials"],
        queryFn: fetchElectedOfficials,
        staleTime: Infinity,
        enabled: true,
    })
